import { formatDate } from '@angular/common';
import { CellClickedEvent, ColDef } from 'ag-grid-community';

import {
  compareAsFloats,
  compareAsIntegers,
  formatCount,
  formatCurrency,
  formatQuantity,
} from 'app/shared/utils/number.utils';
import { compareAlphabetically } from 'app/shared/utils/string.utils';
import {
  AgGridRouterLinkCellComponent,
  AgGridRouterLinkCellParamRouterLinkFormatter,
  IAgGridRouterLinkCellParams,
} from 'v2/modules/shared/components/ag-grid-router-link-cell/ag-grid-router-link-cell.component';

import { compareAsDates } from './date.utils';

export interface BuildColDefOptions {
  /**
   * Set to `true` to specify that the provided `filterParams` should be merged
   * with the default filter params.
   *
   * @default false
   */
  mergeFilterParams ?:boolean;
  /**
   * Set to `true` to tell AG Grid to allow the text in the column to wrap to
   * new lines and expand the row to contain the wrapped content.
   *
   * @default false
   */
  multiline ?:boolean;
  /**
   * A string where `:VALUE:` is replaced with the URL encoded string value of
   * the field, or a formatter function which receives the url encoded `value`
   * and should return a string.  Providing this will wrap the output in an
   * anchor with the provided href
   */
  routerLinkFormatter ?:AgGridRouterLinkCellParamRouterLinkFormatter;
  routerLinkOptions ?:IAgGridRouterLinkCellParams['routerLinkOptions'];
  /**
   * The property in the row's `data` to look for the field on.  If unspecified,
   * it is assumed the field is an immediate member of the row's `data`.
   *
   * Defaults to the value of `ColDef.field` if `valueGetterField` is specified.
   *
   * Note: AG Grid supports dot-syntax to specify field location.  E.g. a
   * `ColDef` could specify `field: 'lot.lotNumber` and the default `valueGetter`
   * would essentially be `({ data }) => data.lot.lotNumber`, so this field will
   * seldom be necessary.
   */
  valueGetterDataKey ?:string;
  /**
   * Overrides `ColDef.field` for the value getter if specified.
   *
   * The property of the row's data that should be used as the field value.  This
   * does the same as the `ColDef.field` property, but without specifying the
   * field, in the final ColDef returned.
   *
   * Defaults to the value of `ColDef.field`.
   */
  valueGetterField ?:string;
}

export enum ColType {
  DEFAULT = 'DEFAULT',
  NULL = 'NULL',

  ACTIONS = 'ACTIONS',
  BOOLEAN = 'BOOLEAN',
  CHECKBOX = 'CHECKBOX',
  CURRENCY = 'CURRENCY',
  /** Note: Use `DATETIME` unless you're _sure_ the time isn't relevant. */
  DATE = 'DATE',
  DATETIME = 'DATETIME',
  EXPAND = 'EXPAND',
  INTEGER = 'INGEGER',
  QUANTITY = 'QUANTITY',
  UOM = 'UOM',
  /** Note: Use `DATETIME` unless you're _sure_ the date isn't relevant. */
  TIME = 'TIME',
}

export const COLUMN_ACTIONS :ColDef = {
  colId: ColType.ACTIONS,
  cellClass: 'lum-ag-actions-cell  text-center',
  editable: false,
  filter: false,
  filterParams: null,
  floatingFilter: false,
  headerClass: 'lum-ag-actions-cell  lum-ag-actions-cell--header  text-center',
  lockPinned: true,
  lockPosition: true,
  pinned: 'right',
  resizable: false,
  sortable: false,
  suppressColumnsToolPanel: true,
  suppressMenu: true,
  suppressMovable: true,
  suppressNavigable: true,
  width: 50,
  onCellClicked: (params :CellClickedEvent) => {
    params.event.stopPropagation();
    params.event.preventDefault();
  },
};

const COLUMN_CHECKBOX :ColDef = {
  colId: ColType.CHECKBOX,
  cellClass: 'lum-ag-checkbox-cell  text-center',
  checkboxSelection: true,
  filter: false,
  filterParams: null,
  floatingFilter: false,
  headerCheckboxSelection: true,
  headerClass: 'lum-ag-checkbox-cell  lum-ag-checkbox-cell--header  text-center',
  lockPinned: true,
  lockPosition: true,
  pinned: 'left',
  resizable: false,
  sortable: false,
  suppressColumnsToolPanel: true,
  suppressMenu: true,
  suppressMovable: true,
  suppressNavigable: true,
  width: 32,
  maxWidth: 32,
  minWidth: 32,
  onCellClicked: (params :CellClickedEvent) => {
    params.event.stopPropagation();
    params.event.preventDefault();
  },
};

const COLUMN_DEFAULT :ColDef = {
  comparator: compareAlphabetically,
  filter: 'agTextColumnFilter',
  filterParams: {
    filterOptions: [
      'blank',
      'notBlank',
      'contains',
      'notContains',
      'equals',
      'notEqual',
    ],
    defaultOption: 'contains',
    maxNumConditions: 1,
  },
  // Please set `floatingFilter` using the `defaultColDef` grid option.
  // floatingFilter: true,
  resizable: true,
  sortable: true,
};

const COLUMN_DATETIME :ColDef = {
  ...COLUMN_DEFAULT,
  comparator: compareAsDates,
  filter: 'agDateColumnFilter',
  filterParams: {
    filterOptions: [
      'blank',
      'notBlank',
      'equals',
      'notEqual',
      'greaterThan',
      'lessThan',
      'inRange',
    ],
    defaultOption: 'equals',
    maxNumConditions: 1,
  },
  filterValueGetter: ({ colDef, data }) => new Date(data[colDef.field]).valueOf(),
  sortingOrder: ['desc', 'asc', null], // prioritize most recent first when sorting dates
  valueFormatter: ({ value }) => formatDate(value, 'short', 'en-US'),
};

const COLUMN_EXPAND :ColDef = {
  cellClass: 'lum-ag-expand-cell',
  cellRenderer: 'agGroupCellRenderer',
  filter: false,
  filterParams: null,
  floatingFilter: false,
  headerClass: 'lum-ag-expand-cell  lum-ag-expand-cell--header',
  lockPinned: true,
  lockPosition: true,
  maxWidth: 24,
  minWidth: 24,
  pinned: 'left',
  resizable: false,
  sortable: false,
  suppressColumnsToolPanel: true,
  suppressMenu: true,
  suppressMovable: true,
  suppressNavigable: true,
  width: 24,
};

const COLUMN_NUMBER :ColDef = {
  ...COLUMN_DEFAULT,
  cellClass: 'text-right',
  comparator: compareAsFloats,
  filter: 'agNumberColumnFilter',
  filterParams: {
    filterOptions: [
      'blank',
      'notBlank',
      'equals',
      'notEqual',
      'greaterThan',
      'greaterThanOrEqual',
      'lessThan',
      'lessThanOrEqual',
      'inRange',
    ],
    defaultOption: 'equals',
    maxNumConditions: 1,
  },
  filterValueGetter: ({ colDef, getValue }) => parseFloat(getValue(colDef.field)),
  valueFormatter: ({ value }) => formatQuantity(value),
};

const ColDefByColType :Partial<Record<ColType, ColDef>> = {
  [ColType.ACTIONS]: { ...COLUMN_ACTIONS },
  [ColType.BOOLEAN]: {
    ...COLUMN_DEFAULT,
    filterValueGetter: ({ colDef, getValue }) => Boolean(getValue(colDef.field)),
  },
  [ColType.CHECKBOX]: { ...COLUMN_CHECKBOX },
  [ColType.CURRENCY]: {
    ...COLUMN_NUMBER,
    valueFormatter: ({ value }) => formatCurrency(value),
  },
  [ColType.DATE]: {
    ...COLUMN_DATETIME,
    valueFormatter: ({ value }) =>
      value ? formatDate(value, 'shortDate', 'en-US') : '',
  },
  [ColType.DATETIME]: {
    ...COLUMN_DATETIME,
    valueFormatter: ({ value }) =>
      value ? formatDate(value, 'short', 'en-US') : '',
  },
  [ColType.EXPAND]: { ...COLUMN_EXPAND },
  [ColType.INTEGER]: {
    ...COLUMN_NUMBER,
    comparator: compareAsIntegers,
    filterValueGetter: ({ colDef, getValue }) =>
      parseInt(getValue(colDef.field), 10),
    valueFormatter: ({ value }) => formatCount(value),
  },
  [ColType.NULL]: {},
  [ColType.QUANTITY]: { ...COLUMN_NUMBER },
  [ColType.TIME]: {
    ...COLUMN_DATETIME,
    valueFormatter: ({ value }) =>
      value ? formatDate(value, 'shortTime', 'en-US') : '',
  },
};

export function buildColDefRouterLinkCellRendererAndParams(
  routerLinkFormatter :AgGridRouterLinkCellParamRouterLinkFormatter,
  routerLinkOptions ?:IAgGridRouterLinkCellParams['routerLinkOptions'],
) :Partial<ColDef> | null {
  if (!routerLinkFormatter) {
    return null;
  }

  return {
    cellRenderer: AgGridRouterLinkCellComponent,
    cellRendererParams: { routerLinkFormatter, routerLinkOptions },
  };
}
export function buildColDefValueGetter(
  dataKey :string,
  field ?:string,
) :Partial<ColDef> | null {
  if (typeof dataKey !== 'string') {
    return null;
  }

  const fieldPath = field.split('.');

  field = fieldPath.length > 1 ? fieldPath[fieldPath.length - 1] : field;

  return {
    valueGetter: ({ colDef, data }) => data?.[dataKey]?.[field ?? colDef.field],
  };
}

export function buildColDef(
  type :ColType,
  def ?:Partial<ColDef>,
  options ?:BuildColDefOptions,
) :ColDef {
  options = Object.assign(
    {
      // DEFAULTS
      mergeFilterParams: false,
      multiline: false,
      routerLinkFormatter: null,
      valueGetterDataKey: null,
      valueGetterField: def?.field ?? null,
    },
    options,
  );

  const cellRenderer :Partial<ColDef> = options.routerLinkFormatter
    ? buildColDefRouterLinkCellRendererAndParams(
        options.routerLinkFormatter,
        options.routerLinkOptions,
      )
    : null;

  const { valueGetterDataKey, valueGetterField } = options;
  const valueGetter =
    def?.field && valueGetterDataKey
      ? buildColDefValueGetter(valueGetterDataKey, valueGetterField)
      : null;

  const baseDef = ColDefByColType[type] ?? COLUMN_DEFAULT;

  if (options.multiline) {
    Object.assign(baseDef, {
      autoHeight: true,
      wrapText: true,
    });
  }

  if (def?.filterParams && options.mergeFilterParams) {
    def.filterParams = {
      ...baseDef.filterParams,
      ...def.filterParams,
    };
  }

  return {
    ...baseDef,
    ...cellRenderer,
    ...valueGetter,
    ...def, // the original def last, so the consumer can override anything
  };
}

export function columnSize(flex :number, minWidth ?:number) :Partial<ColDef> {
  return {
    flex,
    // in general we don't want to _force_ any column to be larger than 200px
    minWidth: minWidth ?? Math.min(flex * 10, 200),
  };
}
